import { supabase, isConfigured } from './supabase';

export const ARCHIVE_DEFAULT_BASE_PATH = 'internal-client-archive/';
export const ARCHIVE_DEFAULT_BUCKET = 'feria-archive';

export const ARCHIVE_DOCUMENT_CATEGORIES = [
  { value: 'datos_cliente', label: 'Datos del cliente', pathSegment: '01_datos_cliente' },
  { value: 'cotizaciones', label: 'Cotizaciones', pathSegment: '02_cotizaciones' },
  { value: 'contratos', label: 'Contratos', pathSegment: '03_contratos' },
  { value: 'brief', label: 'Brief', pathSegment: '04_brief' },
  { value: 'pagos', label: 'Pagos', pathSegment: '05_pagos' },
  { value: 'archivos_cliente', label: 'Archivos subidos por el cliente', pathSegment: '06_archivos_cliente' },
  { value: 'proyecto', label: 'Proyecto', pathSegment: '07_proyecto' },
  { value: 'entregables', label: 'Entregables', pathSegment: '08_entregables' },
  { value: 'brand_kit', label: 'Brand Kit', pathSegment: '09_brand_kit' },
  { value: 'comunicaciones', label: 'Comunicaciones', pathSegment: '10_comunicaciones' },
  { value: 'otros', label: 'Otros', pathSegment: '99_otros' },
];

export const ARCHIVE_DOCUMENT_TYPES = [
  { value: 'client_id_document', label: 'Cedula / ID' },
  { value: 'ruc_document', label: 'RUC' },
  { value: 'legal_document', label: 'Documento legal' },
  { value: 'quote_snapshot', label: 'Snapshot de cotizacion' },
  { value: 'quote_pdf', label: 'PDF de cotizacion' },
  { value: 'selected_package', label: 'Paquete seleccionado' },
  { value: 'signed_contract', label: 'Contrato firmado' },
  { value: 'contract_pdf', label: 'PDF de contrato' },
  { value: 'brief_answers', label: 'Respuestas de brief' },
  { value: 'brief_pdf', label: 'PDF de brief' },
  { value: 'payment_receipt', label: 'Comprobante de pago' },
  { value: 'invoice', label: 'Factura' },
  { value: 'client_uploaded_file', label: 'Archivo del cliente' },
  { value: 'project_file', label: 'Archivo de proyecto' },
  { value: 'deliverable', label: 'Entregable' },
  { value: 'brand_kit', label: 'Brand Kit' },
  { value: 'communication_log', label: 'Registro de comunicacion' },
  { value: 'other', label: 'Otro' },
];

export const SENSITIVE_ARCHIVE_DOCUMENT_TYPES = ['client_id_document', 'ruc_document', 'legal_document'];
export const FINANCIAL_ARCHIVE_DOCUMENT_TYPES = ['payment_receipt'];
export const LEGAL_ARCHIVE_DOCUMENT_TYPES = ['signed_contract', 'contract_pdf'];

export const ARCHIVE_STORAGE_PROVIDERS = [
  { value: 'supabase', label: 'Supabase Storage' },
  { value: 's3_compatible', label: 'S3 compatible (futuro)' },
  { value: 'external_server', label: 'Servidor externo (futuro)' },
];

export const ARCHIVE_STORAGE_MODES = [
  { value: 'direct', label: 'Directo' },
  { value: 'queued_future', label: 'Cola futura' },
];

const CATEGORY_BY_VALUE = ARCHIVE_DOCUMENT_CATEGORIES.reduce((acc, item) => {
  acc[item.value] = item;
  return acc;
}, {});

const TYPE_BY_VALUE = ARCHIVE_DOCUMENT_TYPES.reduce((acc, item) => {
  acc[item.value] = item;
  return acc;
}, {});

const SENSITIVE_TYPE_SET = new Set(SENSITIVE_ARCHIVE_DOCUMENT_TYPES);
const FINANCIAL_TYPE_SET = new Set(FINANCIAL_ARCHIVE_DOCUMENT_TYPES);
const LEGAL_TYPE_SET = new Set(LEGAL_ARCHIVE_DOCUMENT_TYPES);
const ACCESS_LEVEL_SET = new Set(['standard', 'sensitive', 'financial', 'legal']);

function cleanText(value, fallback = '') {
  const text = typeof value === 'string' ? value.trim() : '';
  return text || fallback;
}

function isUuid(value) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(String(value || '').trim());
}

function archiveStepError(step, error) {
  const rawMessage = error?.message || String(error || 'Error desconocido');
  const lower = rawMessage.toLowerCase();
  if (lower.includes('row-level security') || lower.includes('permission') || lower.includes('unauthorized') || error?.statusCode === '403') {
    return new Error(`No se pudo subir el archivo: la cuenta actual no tiene permisos para Archivo interno (${step}). Inicia sesión con un usuario interno admin, CRM, creativo o finanzas.`);
  }
  if (lower.includes('invalid input syntax for type uuid')) {
    return new Error(`No se pudo registrar el archivo interno (${step}): uno de los identificadores no es válido. Actualiza la sesión e intenta nuevamente.`);
  }
  return new Error(`No se pudo subir el archivo (${step}): ${rawMessage}`);
}

export function isSensitiveArchiveDocument(document = {}) {
  return document?.access_level === 'sensitive' || SENSITIVE_TYPE_SET.has(document?.document_type);
}

export function isFinancialArchiveDocument(document = {}) {
  return document?.access_level === 'financial'
    || FINANCIAL_TYPE_SET.has(document?.document_type)
    || document?.source === 'payment_receipt_uploaded';
}

export function isLegalArchiveDocument(document = {}) {
  return document?.access_level === 'legal'
    || LEGAL_TYPE_SET.has(document?.document_type)
    || document?.source === 'contract_signed';
}

function normalizeArchiveAccessLevel(accessLevel, documentType) {
  if (SENSITIVE_TYPE_SET.has(documentType)) return 'sensitive';
  if (FINANCIAL_TYPE_SET.has(documentType)) return 'financial';
  if (LEGAL_TYPE_SET.has(documentType)) return 'legal';
  const requested = cleanText(accessLevel, 'standard');
  return ACCESS_LEVEL_SET.has(requested) ? requested : 'standard';
}

function normalizeBasePath(value) {
  const raw = cleanText(value, ARCHIVE_DEFAULT_BASE_PATH)
    .replace(/^\/+/, '')
    .replace(/\/+$/, '');
  return `${raw || ARCHIVE_DEFAULT_BASE_PATH.replace(/\/+$/, '')}/`;
}

export function normalizeArchiveConfig(config = {}) {
  return {
    enabled: config.archive_storage_enabled !== false,
    provider: cleanText(config.archive_storage_provider, 'supabase'),
    bucket: cleanText(config.archive_storage_bucket, ARCHIVE_DEFAULT_BUCKET),
    basePath: normalizeBasePath(config.archive_storage_base_path || config.base_path),
    mode: cleanText(config.archive_storage_mode, 'direct'),
    endpoint: cleanText(config.archive_storage_endpoint),
    region: cleanText(config.archive_storage_region),
    accessKeyRef: cleanText(config.archive_storage_access_key_ref),
    secretKeyRef: cleanText(config.archive_storage_secret_key_ref),
  };
}

export function slugifyArchiveSegment(value, fallback = 'sin-nombre') {
  const text = cleanText(value, fallback);
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80) || fallback;
}

function todayIso(date = new Date()) {
  return date.toISOString().slice(0, 10);
}

function fileExtension(fileName = '') {
  const safe = cleanText(fileName);
  const dot = safe.lastIndexOf('.');
  return dot >= 0 ? safe.slice(dot + 1).toLowerCase().replace(/[^a-z0-9]/g, '') : '';
}

function fileStem(fileName = '') {
  const safe = cleanText(fileName, 'archivo');
  const dot = safe.lastIndexOf('.');
  return dot >= 0 ? safe.slice(0, dot) : safe;
}

function safeStoredFileName({ file, documentType, cliente, proyecto, date }) {
  const originalName = cleanText(file?.name, 'archivo');
  const extension = fileExtension(originalName);
  const originalStem = slugifyArchiveSegment(fileStem(originalName), 'archivo');
  const typeSlug = slugifyArchiveSegment(documentType, 'documento');
  const entitySlug = slugifyArchiveSegment(
    proyecto?.nombre || proyecto?.name || proyecto?.package_name || cliente?.nombre || cliente?.name,
    'cliente'
  );
  const suffix = Date.now().toString(36);
  const stem = [date, typeSlug, entitySlug, originalStem, suffix].filter(Boolean).join('_').slice(0, 180);
  return extension ? `${stem}.${extension}` : stem;
}

export function buildArchivePath({
  file,
  cliente,
  clienteId,
  proyecto,
  proyectoId,
  category = 'otros',
  documentType = 'other',
  config = {},
  date = new Date(),
} = {}) {
  const archiveConfig = normalizeArchiveConfig(config);
  const datePart = todayIso(date);
  const year = String(date.getFullYear());
  const clientSlug = slugifyArchiveSegment(
    cliente?.nombre || cliente?.name || cliente?.empresa || cliente?.brand || clienteId,
    clienteId || 'cliente'
  );
  const projectSlug = slugifyArchiveSegment(
    proyecto?.nombre || proyecto?.name || proyecto?.package_name || proyecto?.fase || proyectoId,
    ''
  );
  const projectFolder = projectSlug ? `${datePart}_${projectSlug}` : 'general';
  const categoryMeta = CATEGORY_BY_VALUE[category] || CATEGORY_BY_VALUE.otros;
  const storedFileName = safeStoredFileName({ file, documentType, cliente, proyecto, date: datePart });
  const storagePath = [
    archiveConfig.basePath,
    clientSlug,
    year,
    projectFolder,
    categoryMeta.pathSegment,
    storedFileName,
  ].join('/').replace(/\/+/g, '/');

  return {
    storagePath,
    storedFileName,
    basePath: archiveConfig.basePath,
    clientSlug,
    projectSlug,
    categoryPathSegment: categoryMeta.pathSegment,
  };
}

async function save({
  file,
  clienteId,
  proyectoId = null,
  cliente = null,
  proyecto = null,
  category = 'otros',
  documentType = 'other',
  source = 'manual_upload',
  metadata = {},
  notes = '',
  displayName = '',
  uploadedBy = null,
  accessLevel = '',
  config = {},
} = {}) {
  if (!isConfigured) {
    throw new Error('Supabase no esta configurado para el archivo interno.');
  }
  if (!file) {
    throw new Error('Selecciona un archivo para guardar en el archivo interno.');
  }
  if (!clienteId) {
    throw new Error('El cliente es obligatorio para archivar documentos.');
  }

  const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
  if (sessionError) {
    throw archiveStepError('sesion', sessionError);
  }
  const sessionUserId = sessionData?.session?.user?.id || null;
  if (!sessionUserId) {
    throw new Error('No se pudo subir el archivo: inicia sesión con una cuenta interna real. Las cuentas demo/locales no pueden guardar archivos en producción.');
  }
  const safeUploadedBy = isUuid(uploadedBy) ? uploadedBy : sessionUserId;

  const archiveConfig = normalizeArchiveConfig(config);
  if (!archiveConfig.enabled) {
    throw new Error('El archivo interno esta desactivado en configuracion.');
  }
  if (archiveConfig.provider !== 'supabase') {
    throw new Error('Phase 1 solo soporta Supabase Storage como proveedor de archivo.');
  }

  const safeCategory = CATEGORY_BY_VALUE[category] ? category : 'otros';
  const safeDocumentType = TYPE_BY_VALUE[documentType] ? documentType : 'other';
  const safeAccessLevel = normalizeArchiveAccessLevel(accessLevel, safeDocumentType);
  const builtPath = buildArchivePath({
    file,
    cliente,
    clienteId,
    proyecto,
    proyectoId,
    category: safeCategory,
    documentType: safeDocumentType,
    config: archiveConfig,
  });

  const { error: uploadError } = await supabase.storage
    .from(archiveConfig.bucket)
    .upload(builtPath.storagePath, file, {
      cacheControl: '3600',
      upsert: false,
      contentType: file.type || undefined,
    });

  if (uploadError) throw archiveStepError('storage', uploadError);

  const archivedAt = new Date().toISOString();
  const registryRow = {
    cliente_id: clienteId,
    proyecto_id: proyectoId || null,
    document_type: safeDocumentType,
    document_category: safeCategory,
    display_name: cleanText(displayName, file.name || TYPE_BY_VALUE[safeDocumentType]?.label || 'Documento interno'),
    original_file_name: file.name || null,
    stored_file_name: builtPath.storedFileName,
    storage_provider: archiveConfig.provider,
    storage_bucket: archiveConfig.bucket,
    base_path: builtPath.basePath,
    storage_path: builtPath.storagePath,
    internal_url: null,
    visibility: 'internal',
    access_level: safeAccessLevel,
    uploaded_by: safeUploadedBy || null,
    source: cleanText(source, 'manual_upload'),
    archive_status: 'stored',
    archived_at: archivedAt,
    metadata: {
      ...(metadata || {}),
      notes: cleanText(notes),
      client_slug: builtPath.clientSlug,
      project_slug: builtPath.projectSlug,
      category_path_segment: builtPath.categoryPathSegment,
      mime_type: file.type || null,
      file_size: typeof file.size === 'number' ? file.size : null,
    },
    updated_at: archivedAt,
  };

  const { data, error } = await supabase
    .from('internal_client_documents')
    .insert(registryRow)
    .select('*')
    .single();

  if (error) {
    try {
      const { error: cleanupError } = await supabase.storage
        .from(archiveConfig.bucket)
        .remove([builtPath.storagePath]);
      if (cleanupError) {
        console.error('[archiveStorage.save] No se pudo limpiar archivo huerfano tras fallo de registro:', {
          cleanupError,
          registryError: error,
          storageBucket: archiveConfig.bucket,
          storagePath: builtPath.storagePath,
        });
      }
    } catch (cleanupError) {
      console.error('[archiveStorage.save] Error limpiando archivo huerfano tras fallo de registro:', {
        cleanupError,
        registryError: error,
        storageBucket: archiveConfig.bucket,
        storagePath: builtPath.storagePath,
      });
    }
    throw archiveStepError('registro', error);
  }
  return data;
}

async function get(documentId) {
  if (!documentId) throw new Error('Documento requerido.');
  const { data, error } = await supabase
    .from('internal_client_documents')
    .select('*')
    .eq('id', documentId)
    .single();
  if (error) throw error;
  return data;
}

async function listByClient(clienteId, { proyectoId, category, includeArchived = false } = {}) {
  if (!clienteId) return [];
  let query = supabase
    .from('internal_client_documents')
    .select('*')
    .eq('cliente_id', clienteId)
    .order('created_at', { ascending: false });

  if (proyectoId) query = query.eq('proyecto_id', proyectoId);
  if (category && category !== 'all') query = query.eq('document_category', category);
  if (!includeArchived) query = query.neq('archive_status', 'archived');

  const { data, error } = await query;
  if (error) throw error;
  return data || [];
}

async function getSignedUrl(documentId, expiresIn = 60 * 60) {
  const document = await get(documentId);
  if (document.storage_provider !== 'supabase') {
    throw new Error('Este proveedor todavia no soporta URLs firmadas en Feria OS.');
  }
  const { data, error } = await supabase.storage
    .from(document.storage_bucket || ARCHIVE_DEFAULT_BUCKET)
    .createSignedUrl(document.storage_path, expiresIn);
  if (error) throw error;
  return {
    document,
    signedUrl: data?.signedUrl || '',
    expiresAt: new Date(Date.now() + expiresIn * 1000).toISOString(),
  };
}

async function getPublicOrSignedUrl(documentId, expiresIn = 60 * 60) {
  return getSignedUrl(documentId, expiresIn);
}

async function deleteOrArchive(documentId) {
  if (!documentId) throw new Error('Documento requerido.');
  const now = new Date().toISOString();
  const { data, error } = await supabase
    .from('internal_client_documents')
    .update({ archive_status: 'archived', updated_at: now })
    .eq('id', documentId)
    .select('*')
    .single();
  if (error) throw error;
  return data;
}

export const archiveStorage = {
  save,
  get,
  listByClient,
  getSignedUrl,
  getPublicOrSignedUrl,
  deleteOrArchive,
  buildArchivePath,
  normalizeArchiveConfig,
};

export default archiveStorage;
